import { Fragment, useEffect, useMemo, useState } from 'react';
import { useResource } from '@limuzin/kit';
import type { PlaceView, SavedPlaceView } from '@limuzin/passenger';
import { getSavedPlaces, searchPlaces } from '../api';
import { useSession } from '../session';
import { Icon } from '../material';
import { Sheet } from '../widgets';

/** One row: a saved place, a catalogue entry, or the words the passenger typed. */
interface Suggestion {
  name: string;
  sub: string;
  /** What a tap hands back. Not always the name: a saved place hands back its address — and see `fills`. */
  value: string;
  saved: boolean;
  typed: boolean;
}

/**
 * «Куда едем?» — the address sheet, and «Адрес» in «Мои адреса», which is the same
 * sheet: an address hunted down for an order and the same address saved for later
 * should not be two different exercises. A port of `place_picker.dart`.
 *
 * The catalogue is offered first and free text is never refused: a small town's
 * catalogue is always incomplete, and a passenger who cannot order is worse than a
 * row nobody can count. So what was typed is a row of its own under the matches, and
 * picking it sends those words as they are — they become a suggestion the operator
 * sees, which is how the catalogue grows.
 *
 * Filling a saved place, two things differ: the saved places are left out — this is
 * the sheet that writes one — and a catalogue row hands over its street rather than
 * its name, because the field being filled is «Адрес» and «Вокзал» is the passenger's
 * own word to put in «Название» if they want it.
 *
 * Every row is one tap and the sheet closes on it. There is no button to confirm with.
 */
export function PlacePicker({
  title,
  initial,
  fills = 'route',
  onPick,
  onClose,
}: {
  title: string;
  initial: string;
  fills?: 'route' | 'address';
  onPick: (address: string) => void;
  onClose: () => void;
}) {
  const { api, me } = useSession();
  const townId = me?.passenger?.townId ?? null;
  const route = fills === 'route';

  const [query, setQuery] = useState(initial);
  const [debounced, setDebounced] = useState(initial);

  // Long enough that a fast typist makes one request rather than nine.
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(query), 220);
    return () => window.clearTimeout(timer);
  }, [query]);

  const found = useResource<PlaceView[]>(
    () => (townId ? searchPlaces(api, townId, debounced.trim()) : Promise.resolve([])),
    [api, townId, debounced],
    { enabled: Boolean(townId) },
  );

  const saved = useResource<SavedPlaceView[]>(
    () => (route ? getSavedPlaces(api) : Promise.resolve([])),
    [api, route],
  );

  const typed = query.trim();

  const rows = useMemo(() => {
    const needle = typed.toLowerCase();
    const list: Suggestion[] = [
      ...(saved.data ?? [])
        .filter(
          (place) =>
            !needle || `${place.label} ${place.address}`.toLowerCase().includes(needle),
        )
        .map((place) => ({
          name: place.label,
          sub: place.address,
          // «Дом» is the passenger's own word for the place; the driver and the ride
          // card get the street it stands for, never the word.
          value: place.address,
          saved: true,
          typed: false,
        })),
      ...(found.data ?? []).map((place) => ({
        name: place.name,
        sub: place.address,
        // A catalogue row with no street of its own falls back to its name, which is
        // an address as far as the operator reading the order is concerned.
        value: route || !place.address.trim() ? place.name : place.address,
        saved: false,
        typed: false,
      })),
    ];
    // What was typed and matched nothing is an address too. It goes last, under the
    // rows the catalogue does know: those are the likelier answer.
    // Typing a saved place's name is choosing that place, not sending the word.
    if (
      needle &&
      !list.some(
        (row) => row.value.toLowerCase() === needle || (row.saved && row.name.toLowerCase() === needle),
      )
    ) {
      list.push({
        name: typed,
        sub: route ? 'Отправить как есть' : 'Сохранить как есть',
        value: typed,
        saved: false,
        typed: true,
      });
    }
    return list;
  }, [saved.data, found.data, typed, route]);

  // A heading over the first row of each group, where there are two to tell apart:
  // the passenger's own places, and everything the town and the keyboard offer.
  const grouped = rows.length > 0 && rows[0]!.saved && !rows[rows.length - 1]!.saved;

  return (
    <Sheet title={title} onClose={onClose}>
      <input
        className="sfield"
        placeholder="Улица, дом или название"
        value={query}
        autoFocus
        enterKeyHint="done"
        onChange={(event) => setQuery(event.target.value)}
        onKeyDown={(event) => {
          if (event.key !== 'Enter' || !typed) return;
          // Enter on «Дом» is the saved place too, and it sends its address.
          const named = rows.find((row) => row.saved && row.name.toLowerCase() === typed.toLowerCase());
          onPick(named ? named.value : typed);
        }}
      />

      <div className="picker-rows">
        {rows.length === 0 ? (
          <p className="body" style={{ padding: '18px 0', margin: 0, fontSize: 14.5 }}>
            {found.busy ? 'Ищем…' : 'Начните вводить адрес'}
          </p>
        ) : (
          rows.map((row, i) => (
            <Fragment key={`${row.saved ? 's' : row.typed ? 't' : 'c'}-${i}-${row.name}`}>
              {grouped && i === 0 && <div className="label picker-group first">Мои адреса</div>}
              {grouped && i > 0 && rows[i - 1]!.saved && !row.saved && (
                <div className="label picker-group">Другие адреса</div>
              )}
              <button type="button" className="picker-row" onClick={() => onPick(row.value)}>
                {/* What kind of place it is, rather than its first letter. */}
                <span className={row.saved ? 'picker-mark tint' : 'picker-mark'}>
                  <Icon
                    name={row.saved ? 'bookmark_rounded' : row.typed ? 'edit_outlined' : 'place_outlined'}
                    size={17}
                  />
                </span>
                <span className="grow">
                  <span className="picker-name">{row.name}</span>
                  <span className="caption ellipsis picker-sub">{row.sub}</span>
                </span>
              </button>
            </Fragment>
          ))
        )}
      </div>
    </Sheet>
  );
}
